import { useMemo } from "react";
import * as React from "react";
import { View, StyleSheet } from "react-native";
import { startCase, orderBy } from "lodash-es";

import { Typography, useTheme } from "@smartrent/ui";

import Helpers from "@/lib/helpers";

import { UserProps } from "@/types";

import { useGetUserWebhooksQuery } from "@/api/users";
import { ScrollArea } from "@/_v2/components/ui/scroll-area";

export interface EmailWebhooksProps {
  user: UserProps;
}

export const EmailWebhooks: React.FC<
  React.PropsWithChildren<EmailWebhooksProps>
> = ({ user }) => {
  const { colors } = useTheme();
  const { data, isLoading } = useGetUserWebhooksQuery(user);

  const webhooks = useMemo(() => {
    return data?.webhooks ?? [];
  }, [data]);

  const orderedWebhooks = orderBy(webhooks || [], ["inserted_at"], ["desc"]);

  const getColorForEvent = (event: string) => {
    switch (event.toLowerCase()) {
      // for success events we style them green
      case "open":
      case "click":
      case "delivered":
        return "success";
      // for failure events we style them red
      case "bounce":
      case "dropped":
        return "error";
      // for other events we style them grey
      default:
        return "textSecondary";
    }
  };

  return (
    <ScrollArea className="h-full max-h-80 px-1 rounded-sm">
      {isLoading ? <Typography>Loading...</Typography> : null}
      {!orderedWebhooks.length && !isLoading ? (
        <Typography color="mutedTextGrayscale">No webhooks found</Typography>
      ) : null}
      {orderedWebhooks.map(({ subject, event, inserted_at, response }, idx) => (
        <View
          key={idx}
          style={[styles.emailRow, { borderColor: colors.borderLow }]}
        >
          <Typography style={styles.title} variation="bold.title.six">
            {subject || "(no subject)"}
          </Typography>
          <View>
            <Typography style={styles.smallText}>
              {
                <Typography
                  style={[styles.bold, styles.smallText]}
                  color={getColorForEvent(event)}
                >
                  {startCase(event)}
                </Typography>
              }{" "}
              at {Helpers.formatDate(inserted_at)}{" "}
            </Typography>
            {response ? (
              <Typography style={styles.smallText}>
                <Typography style={[styles.bold, styles.smallText]}>
                  Details:{" "}
                </Typography>
                {response}
              </Typography>
            ) : null}
          </View>
        </View>
      ))}
    </ScrollArea>
  );
};

const styles = StyleSheet.create({
  emailRow: {
    paddingVertical: 4,
    borderBottomWidth: 1,
  },
  title: {
    marginBottom: 4,
  },
  bold: {
    fontWeight: "bold",
  },
  smallText: {
    fontSize: 12,
  },
});
